/* ============================================================================
   SEED DATA
   ============================================================================ */

export const CREATOR = {
  handle: 'novasays',
  displayName: 'nova.',
  avatar: '✨',
  bio: 'cult favorites · ships everywhere',
  followers: 512400,
  domain: 'nova.shop',
  accent: '#d4ff47',
};

// The identity fields above describe the store currently on screen. App calls
// this whenever a store loads (the signed-in creator's, or the public demo
// store), so every screen that reads CREATOR.handle / .domain / .displayName
// shows the right account. accent stays the brand colour.
export function setCreatorIdentity({ name, handle, domain, avatar, bio }) {
  const first = String(name || handle || '').trim().split(/\s+/)[0] || handle;
  Object.assign(CREATOR, {
    handle,
    displayName: first.toLowerCase() + '.',
    domain: domain || `skrl.net/${handle}`,
    ...(avatar ? { avatar } : {}),
    ...(bio != null ? { bio } : {}),
  });
}

// Demo photos live in public/demo (public-domain CC0 stock, see CREDITS.json).
// Each one is layered over its gradient, so the gradient still shows while the
// photo loads, and every `background: x.bg` in the app picks the photo up.
export const photo = (file, gradient) => `url(/demo/${file}.jpg) center / cover no-repeat, ${gradient}`;

export const SEED_VIDEOS = [
  {
    id: 'v1',
    caption: 'the oversized hoodie drop you asked for',
    bg: photo('hoodie-back', 'radial-gradient(ellipse at 30% 20%, #ffb4c6 0%, #c4a1ff 40%, #4f3cd9 100%)'),
    motionColor: '#ff6b9d',
    product: {
      id: 'p1', title: 'Pillow Hoodie', subtitle: 'Heather grey · Oversized',
      price: 68, emoji: '🧥', kind: 'physical',
      description: 'Mid-weight brushed fleece. I wear an XS for the oversized look. Limited drop of 180.',
      options: ['XS', 'S', 'M', 'L', 'XL'], optionLabel: 'Size',
      inventory: 84,
      // Image gallery · photo over an accent gradient, emoji as the fallback icon.
      // Order matters · index 0 is the primary image used in lists/feeds.
      images: [
        { id: 'img1a', emoji: '🧥', bg: photo('knit-cream', 'radial-gradient(ellipse at 30% 20%, #ffb4c6, #c4a1ff)'), alt: 'brushed fleece · up close' },
        { id: 'img1b', emoji: '🪡', bg: photo('knit-detail', 'radial-gradient(ellipse at 70% 30%, #c4a1ff, #4f3cd9)'), alt: 'sleeve detail' },
        { id: 'img1c', emoji: '👕', bg: photo('hoodie-street', 'radial-gradient(ellipse at 50% 70%, #ff6b9d, #4f3cd9)'), alt: 'on-body · back' },
        { id: 'img1d', emoji: '🏷️', bg: photo('rack', 'radial-gradient(ellipse at 30% 70%, #ffe8a8, #ffa86b)'), alt: 'wash care label' },
      ],
    },
    stats: { views: 142320, taps: 18410, addToCart: 3204, purchases: 891 },
    posted: '3d',
  },
  {
    id: 'v2',
    caption: 'the serum that saved my 6am flight skin',
    bg: photo('serum-dropper', 'radial-gradient(ellipse at 70% 30%, #ffe8a8 0%, #ffa86b 45%, #ef4370 100%)'),
    motionColor: '#fbbf24',
    product: {
      id: 'p2', title: 'Midnight Serum', subtitle: '30ml · Bakuchiol + Squalane',
      price: 42, emoji: '💧', kind: 'physical',
      description: '2% bakuchiol, squalane, niacinamide. Vegan. Apply at night. Ships in 48h from LA.',
      options: ['30ml'], optionLabel: 'Size',
      inventory: 217,
      images: [
        { id: 'img2a', emoji: '💧', bg: photo('serum-hold', 'radial-gradient(ellipse at 70% 30%, #ffe8a8, #ffa86b)'), alt: 'bottle · front' },
        { id: 'img2b', emoji: '🧴', bg: photo('serum-face', 'radial-gradient(ellipse at 30% 70%, #ffa86b, #ef4370)'), alt: 'pump close-up' },
        { id: 'img2c', emoji: '🌙', bg: photo('vanity', 'radial-gradient(ellipse at 50% 50%, #4f3cd9, #1a0f3d)'), alt: 'evening routine' },
      ],
    },
    stats: { views: 89220, taps: 11230, addToCart: 2104, purchases: 612 },
    posted: '1w',
  },
  {
    id: 'v3',
    caption: 'how I grew 500k followers in 11 months · full system',
    bg: photo('laptop-window', 'radial-gradient(ellipse at 50% 70%, #a8ffd4 0%, #5bd4a9 40%, #0f5a4d 100%)'),
    motionColor: '#22d3ee',
    product: {
      id: 'p3', title: 'Grow Your TikTok · 30 Day System', subtitle: 'Course · 4h video + templates',
      price: 97, emoji: '📈', kind: 'digital',
      description: 'Every hook, format, and posting rhythm I used. 4h of lessons, 40+ hook templates, private Discord. Instant access.',
      options: ['Instant access'], optionLabel: 'Access',
      inventory: 9999,
      images: [
        { id: 'img3a', emoji: '📈', bg: photo('laptop-stats', 'radial-gradient(ellipse at 50% 70%, #a8ffd4, #5bd4a9)'), alt: 'course cover' },
        { id: 'img3b', emoji: '🎥', bg: photo('camera-phone', 'radial-gradient(ellipse at 30% 30%, #5bd4a9, #0f5a4d)'), alt: 'lesson preview' },
        { id: 'img3c', emoji: '📋', bg: photo('notebook-plan', 'radial-gradient(ellipse at 70% 50%, #22d3ee, #0f5a4d)'), alt: 'hook templates' },
        { id: 'img3d', emoji: '💬', bg: photo('phone-hand', 'radial-gradient(ellipse at 50% 30%, #a8ffd4, #22d3ee)'), alt: 'Discord access' },
      ],
    },
    stats: { views: 218400, taps: 42210, addToCart: 8410, purchases: 2104 },
    posted: '2w',
  },
  {
    id: 'v4',
    caption: 'daily greens that don\'t taste like a swamp',
    bg: photo('greens-jar', 'radial-gradient(ellipse at 30% 60%, #c4f0a8 0%, #5bb88a 40%, #2d3a7c 100%)'),
    motionColor: '#84cc16',
    product: {
      id: 'p4', title: 'Daily Greens', subtitle: '30 servings · Mixed berry',
      price: 38, emoji: '🌱', kind: 'physical',
      description: 'Organic spirulina, chlorella, moringa + 14 more. Mixed berry so it doesn\'t taste like guilt.',
      options: ['1 bag', '3 bags · save 15%', '6 bags · save 25%'], optionLabel: 'Bundle',
      inventory: 412,
      images: [
        { id: 'img4a', emoji: '🌱', bg: photo('greens-glass', 'radial-gradient(ellipse at 30% 60%, #c4f0a8, #5bb88a)'), alt: 'bag front' },
        { id: 'img4b', emoji: '🥤', bg: photo('greens-top', 'radial-gradient(ellipse at 70% 30%, #5bb88a, #2d3a7c)'), alt: 'mixed in glass' },
        { id: 'img4c', emoji: '🫐', bg: photo('blueberries', 'radial-gradient(ellipse at 50% 50%, #4f3cd9, #c4a1ff)'), alt: 'mixed berry flavor' },
      ],
    },
    stats: { views: 64200, taps: 6410, addToCart: 1204, purchases: 388 },
    posted: '3w',
  },
  {
    id: 'v5',
    caption: 'the exact presets I use on every video',
    bg: photo('golden-field', 'radial-gradient(ellipse at 70% 70%, #ffc1e3 0%, #a68fff 40%, #2a1b5c 100%)'),
    motionColor: '#e879f9',
    product: {
      id: 'p5', title: 'Cinematic Presets · Vol. 2', subtitle: '12 presets · Lightroom + mobile',
      price: 29, emoji: '🎞️', kind: 'digital',
      description: '12 hand-built presets: warm golden hour, cold cinematic, soft daylight and 9 more. Desktop + mobile files.',
      options: ['Full bundle'], optionLabel: 'Bundle',
      inventory: 9999,
      images: [
        { id: 'img5a', emoji: '🎞️', bg: photo('film-camera', 'radial-gradient(ellipse at 70% 70%, #ffc1e3, #a68fff)'), alt: 'preset cover' },
        { id: 'img5b', emoji: '🌅', bg: photo('golden-grass', 'radial-gradient(ellipse at 30% 30%, #ffe8a8, #ef4370)'), alt: 'golden hour preview' },
        { id: 'img5c', emoji: '❄️', bg: photo('mountain-lake', 'radial-gradient(ellipse at 50% 50%, #c4f0ff, #4f3cd9)'), alt: 'cold cinematic preview' },
        { id: 'img5d', emoji: '☀️', bg: photo('daylight-field', 'radial-gradient(ellipse at 70% 50%, #ffe8a8, #ffa86b)'), alt: 'soft daylight preview' },
      ],
    },
    stats: { views: 48210, taps: 7840, addToCart: 1820, purchases: 612 },
    posted: '1mo',
  },
];

export const SEED_CUSTOMERS = [
  { id: 'c1', name: 'Cara Lin',         email: 'cara.lin@gmail.com',     date: '2h ago', spent: 68,  orderCount: 1, joinedAt: '2025-01-12', tier: 'subscriber', country: 'US' },
  { id: 'c2', name: 'J. Avery',         email: 'j.avery@outlook.com',    date: '5h ago', spent: 97,  orderCount: 2, joinedAt: '2024-11-08', tier: 'returning', country: 'US' },
  { id: 'c3', name: 'Zoe Martinez',     email: 'zoemrtnz@gmail.com',     date: '7h ago', spent: 42,  orderCount: 1, joinedAt: '2025-02-03', tier: 'new',        country: 'CA' },
  { id: 'c4', name: 'River Belmont',    email: 'riverbel@proton.me',     date: '1d ago', spent: 165, orderCount: 4, joinedAt: '2024-08-21', tier: 'vip',        country: 'UK' },
  { id: 'c5', name: 'M. Katz',          email: 'mkatz.designs@gmail.com',date: '1d ago', spent: 29,  orderCount: 1, joinedAt: '2025-02-14', tier: 'new',        country: 'US' },
  { id: 'c6', name: 'Rafi O.',          email: 'rafi.ok@icloud.com',     date: '2d ago', spent: 68,  orderCount: 1, joinedAt: '2024-12-30', tier: 'returning', country: 'AU' },
  { id: 'c7', name: 'P. Ashworth',      email: 'p.ashworth@gmail.com',   date: '2d ago', spent: 38,  orderCount: 1, joinedAt: '2025-01-25', tier: 'new',        country: 'US' },
  { id: 'c8', name: 'Lena Ko',          email: 'lena.ko@hey.com',        date: '3d ago', spent: 97,  orderCount: 2, joinedAt: '2024-10-04', tier: 'subscriber', country: 'KR' },
  { id: 'c9', name: 'Devin Hu',         email: 'devin.hu@gmail.com',     date: '3d ago', spent: 42,  orderCount: 1, joinedAt: '2025-01-30', tier: 'new',        country: 'US' },
  { id: 'c10', name: 'Sunny K.',        email: 'sunnyk@gmail.com',       date: '4d ago', spent: 29,  orderCount: 1, joinedAt: '2025-02-02', tier: 'new',        country: 'US' },
  { id: 'c11', name: 'Maya Reeves',     email: 'maya.r@gmail.com',       date: '5d ago', spent: 312, orderCount: 6, joinedAt: '2024-06-15', tier: 'vip',        country: 'US' },
  { id: 'c12', name: 'Tomi Y.',         email: 't.yamamoto@me.com',      date: '6d ago', spent: 89,  orderCount: 1, joinedAt: '2024-12-12', tier: 'returning', country: 'JP' },
  { id: 'c13', name: 'B. Khoury',       email: 'beirut.k@hotmail.com',   date: '1w ago', spent: 49,  orderCount: 1, joinedAt: '2025-01-05', tier: 'new',        country: 'LB' },
  { id: 'c14', name: 'Sasha Volkov',    email: 'svolk@yandex.com',       date: '1w ago', spent: 244, orderCount: 5, joinedAt: '2024-07-18', tier: 'vip',        country: 'PL' },
  { id: 'c15', name: 'Eli Park',        email: 'eli.park@gmail.com',     date: '2w ago', spent: 39,  orderCount: 1, joinedAt: '2025-01-18', tier: 'new',        country: 'US' },
];

/* SEED_SUBSCRIBERS · email signups (without purchases). Customers who buy are
   automatically subscribers too · this list is JUST the people who haven't
   bought yet but want updates. Source tracks where they came from for
   attribution: which page/post drove the signup.

   Status:
   - active     · receiving emails
   - unsubscribed · clicked unsubscribe link
   - bounced    · email failed to deliver (hard bounce)

   Tags are creator-set labels for segmentation (e.g. "course-waitlist", "VIP").
*/
export const SEED_SUBSCRIBERS = [
  { id: 's1',  email: 'jules.kim@gmail.com',     name: 'Jules Kim',     subscribedAt: '2h ago',  source: 'home page',          tags: ['drop-alerts'],            status: 'active' },
  { id: 's2',  email: 'pixie@hey.com',           name: 'Pixie',         subscribedAt: '5h ago',  source: 'apparel collection', tags: ['drop-alerts'],            status: 'active' },
  { id: 's3',  email: 'morgan.r@gmail.com',      name: 'Morgan R.',     subscribedAt: '8h ago',  source: 'creator kit funnel', tags: ['course-waitlist'],        status: 'active' },
  { id: 's4',  email: 'theahalle@protonmail.com',name: 'Thea Halle',    subscribedAt: '12h ago', source: 'home page',          tags: ['drop-alerts'],            status: 'active' },
  { id: 's5',  email: 'iz.miller@me.com',        name: null,            subscribedAt: '1d ago',  source: 'home page',          tags: [],                         status: 'active' },
  { id: 's6',  email: 'cornelius.q@gmail.com',   name: 'Cornelius Q.',  subscribedAt: '1d ago',  source: 'creator kit funnel', tags: ['course-waitlist', 'VIP'], status: 'active' },
  { id: 's7',  email: 'rae.beck@hotmail.com',    name: 'Rae Beck',      subscribedAt: '2d ago',  source: 'lookbook',           tags: ['drop-alerts'],            status: 'active' },
  { id: 's8',  email: 'noah.lo@icloud.com',      name: 'Noah Lo',       subscribedAt: '2d ago',  source: 'home page',          tags: [],                         status: 'active' },
  { id: 's9',  email: 'mauveandme@gmail.com',    name: null,            subscribedAt: '3d ago',  source: 'apparel collection', tags: ['drop-alerts'],            status: 'unsubscribed' },
  { id: 's10', email: 'finn.de@gmail.com',       name: 'Finn De',       subscribedAt: '3d ago',  source: 'journal',            tags: [],                         status: 'active' },
  { id: 's11', email: 'sun.osei@yahoo.com',      name: 'Sun Osei',      subscribedAt: '4d ago',  source: 'home page',          tags: ['drop-alerts'],            status: 'active' },
  { id: 's12', email: 'a.boateng@gmail.com',     name: 'A. Boateng',    subscribedAt: '5d ago',  source: 'creator kit funnel', tags: ['course-waitlist'],        status: 'active' },
  { id: 's13', email: 'undeliverable@gmail.com', name: null,            subscribedAt: '6d ago',  source: 'home page',          tags: [],                         status: 'bounced' },
  { id: 's14', email: 'sam.takeshi@me.com',      name: 'Sam Takeshi',   subscribedAt: '1w ago',  source: 'lookbook',           tags: ['drop-alerts'],            status: 'active' },
  { id: 's15', email: 'q.alarcon@hey.com',       name: 'Q. Alarcón',    subscribedAt: '1w ago',  source: 'home page',          tags: [],                         status: 'active' },
  { id: 's16', email: 'gulnaz.k@gmail.com',      name: 'Gulnaz K.',     subscribedAt: '1w ago',  source: 'apparel collection', tags: [],                         status: 'active' },
  { id: 's17', email: 'milo.f@yandex.com',       name: 'Milo F.',       subscribedAt: '2w ago',  source: 'creator kit funnel', tags: ['course-waitlist'],        status: 'active' },
  { id: 's18', email: 'tessa.bloom@gmail.com',   name: 'Tessa Bloom',   subscribedAt: '2w ago',  source: 'home page',          tags: ['drop-alerts'],            status: 'active' },
  { id: 's19', email: 'parker.f@me.com',         name: null,            subscribedAt: '3w ago',  source: 'home page',          tags: [],                         status: 'unsubscribed' },
  { id: 's20', email: 'ada.mensah@gmail.com',    name: 'Ada Mensah',    subscribedAt: '3w ago',  source: 'lookbook',           tags: ['drop-alerts'],            status: 'active' },
  { id: 's21', email: 'rivers.k@gmail.com',      name: 'Rivers K.',     subscribedAt: '4w ago',  source: 'home page',          tags: [],                         status: 'active' },
  { id: 's22', email: 'wn.ojeda@hey.com',        name: 'W.N. Ojeda',    subscribedAt: '5w ago',  source: 'creator kit funnel', tags: ['course-waitlist', 'VIP'], status: 'active' },
];

export const SEED_TEXT_POSTS = [
  {
    id: 't1',
    title: 'why I quit my agency job',
    body: 'three years ago I was writing ad copy for protein bars at a brooklyn agency. every friday I\'d send the work to a client who hated everything but couldn\'t say why.\n\nthe course below is everything I wish someone had handed me that year. if you\'re still waiting for permission to leave, this is it.',
    bg: 'radial-gradient(ellipse at 30% 30%, #c4a1ff 0%, #6b3cd9 50%, #1a0a2e 100%)',
    accent: '#d4a1ff',
    mood: '✨',
    productId: 'p3',
    posted: '2d',
    likes: 8420,
  },
  {
    id: 't2',
    title: 'the hoodie took 4 months',
    body: 'the first sample was stiff. the second too thin. the third shrunk in the wash.\n\nthe fourth is the one. mid-weight brushed fleece, cut oversized on purpose, no logo on the chest because you don\'t need to advertise for me.\n\n180 units. that\'s all we could afford this run. when they\'re gone, they\'re gone.',
    bg: 'radial-gradient(ellipse at 70% 40%, #ffb4c6 0%, #d9a4c4 45%, #3a1a2a 100%)',
    accent: '#ffd4e1',
    mood: '🧵',
    productId: 'p1',
    posted: '5d',
    likes: 12480,
  },
  {
    id: 't3',
    title: 'drop 04 · friday 9am pst',
    body: 'new run of the hoodie in stone grey.\nnew tee in cream.\nnew tote that fits a laptop.\n\nemail list gets first access 24h early. if you\'re not on it, now is the time.\n\n(scroll down, hit buy on anything, you\'re in.)',
    bg: 'radial-gradient(ellipse at 50% 50%, #ffe8a8 0%, #d4a147 45%, #2a1f0f 100%)',
    accent: '#ffe8a8',
    mood: '🗓',
    productId: null,
    posted: '1w',
    likes: 4210,
  },
  {
    id: 't4',
    title: 'sarah DMed me this',
    body: '"i bought the course on a wednesday night after a 2-star review from my boss. i watched 3 hours of it that weekend. three weeks later I posted my first video. eight weeks later i had 40k followers. I cried reading the emails."\n\nI still think about it every time i question whether this is worth doing.',
    bg: 'radial-gradient(ellipse at 40% 70%, #a8ffd4 0%, #47a985 45%, #0a2a1f 100%)',
    accent: '#a8ffd4',
    mood: '💌',
    productId: 'p3',
    posted: '2w',
    likes: 18920,
  },
  {
    id: 't5',
    title: '3 ways I style the hoodie',
    body: 'one · with the wide-leg denim and loafers. this is the "I run my own business" look.\n\ntwo · over the slip dress with sneakers. go-to airport outfit.\n\nthree · under the big coat in february. the fleece lining is actually warm.',
    bg: 'radial-gradient(ellipse at 60% 30%, #ffc1e3 0%, #a68fff 40%, #2a1b5c 100%)',
    accent: '#ffc1e3',
    mood: '👗',
    productId: 'p1',
    posted: '3w',
    likes: 6420,
  },
];

export const SEED_IMAGE_POSTS = [
  {
    id: 'i1',
    caption: 'drop 04 · golden hour',
    slides: [
      { bg: photo('golden-field', 'radial-gradient(ellipse at 30% 30%, #ffd4a1 0%, #d9a47c 45%, #3a2414 100%)'), emoji: '🧥' },
      { bg: photo('hoodie-street', 'radial-gradient(ellipse at 70% 60%, #ffb4c6 0%, #a68fff 45%, #2a1b5c 100%)'), emoji: '🧥' },
      { bg: photo('rack-warm', 'radial-gradient(ellipse at 50% 40%, #d4ff47 0%, #84cc16 40%, #1a2a0a 100%)'), emoji: '🧥' },
    ],
    productId: 'p1',
    posted: '1d',
    likes: 24300,
    tag: 'lookbook',
  },
  {
    id: 'i2',
    caption: 'morning routine · the serum',
    slides: [
      { bg: photo('serum-apply', 'radial-gradient(ellipse at 50% 30%, #ffe8a8 0%, #e89a47 45%, #3a240a 100%)'), emoji: '💧' },
      { bg: photo('serum-glow', 'radial-gradient(ellipse at 30% 60%, #a8ffd4 0%, #47d9a8 45%, #0a2a1f 100%)'), emoji: '💧' },
    ],
    productId: 'p2',
    posted: '3d',
    likes: 18200,
    tag: 'routine',
  },
  {
    id: 'i3',
    caption: 'what\'s in the creator kit',
    slides: [
      { bg: photo('chart-paper', 'radial-gradient(ellipse at 40% 40%, #c4a1ff 0%, #6b3cd9 45%, #1a0a2e 100%)'), emoji: '📈' },
      { bg: photo('film-camera', 'radial-gradient(ellipse at 60% 50%, #ffc1e3 0%, #a68fff 40%, #2a1b5c 100%)'), emoji: '🎞️' },
      { bg: photo('notebook-pen', 'radial-gradient(ellipse at 30% 70%, #ffb4c6 0%, #ef4370 45%, #3a0a1a 100%)'), emoji: '✍️' },
      { bg: photo('phone-hand', 'radial-gradient(ellipse at 70% 30%, #a8ffd4 0%, #47a985 45%, #0a2a1f 100%)'), emoji: '📬' },
    ],
    productId: 'p3',
    posted: '5d',
    likes: 9840,
    tag: 'bts',
  },
  {
    id: 'i4',
    caption: 'you all look incredible · UGC',
    slides: [
      { bg: photo('friends-bench', 'radial-gradient(ellipse at 50% 50%, #ffb4c6 0%, #c4a1ff 45%, #3a1a2a 100%)'), emoji: '💕' },
      { bg: photo('friends-dance', 'radial-gradient(ellipse at 40% 60%, #ffe8a8 0%, #ffa86b 45%, #3a1f0a 100%)'), emoji: '🫶' },
      { bg: photo('friends-sun', 'radial-gradient(ellipse at 30% 30%, #a8ffd4 0%, #5bd4a9 40%, #0f2a1d 100%)'), emoji: '✨' },
    ],
    productId: 'p1',
    posted: '1w',
    likes: 31200,
    tag: 'community',
  },
  {
    id: 'i5',
    caption: 'greens · mixed berry · daily',
    slides: [
      { bg: photo('greens-pair', 'radial-gradient(ellipse at 40% 40%, #c4f0a8 0%, #5bb88a 40%, #2d3a7c 100%)'), emoji: '🌱' },
      { bg: photo('berries-glass', 'radial-gradient(ellipse at 60% 30%, #a8ffd4 0%, #47d9a8 45%, #0a2a1f 100%)'), emoji: '🫐' },
    ],
    productId: 'p4',
    posted: '2w',
    likes: 5420,
    tag: 'supplement',
  },
];

export const PAGE_TYPE_META = {
  home:       { label: 'Home',       color: '#a1a1aa', icon: '🏠' },
  product:    { label: 'Product',    color: '#fb923c', icon: '📦' },
  collection: { label: 'Collection', color: '#60a5fa', icon: '🗂' },
  funnel:     { label: 'Funnel',     color: '#d4ff47', icon: '⚡' },
  text:       { label: 'Journal',    color: '#e879f9', icon: '✍' },
  gallery:    { label: 'Gallery',    color: '#f472b6', icon: '🖼' },
};

export const PLATFORM = {
  name: 'skrl',
  tag: 'the vertical-video commerce layer for creators',
};

export const DEFAULT_USER = {
  name: 'Nova',
  handle: 'novasays',
  email: 'nova@nova.shop',
  joined: 'member since 2024',
};

export const DEFAULT_CONNECTIONS = {
  stripe: {
    id: 'stripe', name: 'Stripe', kind: 'Payments',
    desc: 'Accept cards, Apple Pay, Google Pay · required to go live',
    logo: 'S', color: '#635bff',
    connected: false, required: true,
  },
  domain: {
    id: 'domain', name: 'Custom domain', kind: 'Branding',
    desc: 'Connect your own URL so customers land on nova.shop, not skrl.net/nova',
    logo: '◎', color: '#d4ff47',
    connected: true, meta: { name: 'nova.shop', verified: true },
  },
  klaviyo: {
    id: 'klaviyo', name: 'Klaviyo', kind: 'Email marketing',
    desc: 'Auto-sync your email list · send drop announcements and abandoned cart',
    logo: 'K', color: '#0a1c38', dark: true,
    connected: false,
  },
  tiktok: {
    id: 'tiktok', name: 'TikTok', kind: 'Social',
    desc: 'Import videos automatically · track ad attribution from TikTok Ads',
    logo: '♪', color: '#000000', dark: true,
    connected: false,
  },
  shippo: {
    id: 'shippo', name: 'Shippo', kind: 'Fulfillment',
    desc: 'Print shipping labels · automatic tracking emails · returns portal',
    logo: '▲', color: '#60a5fa',
    connected: false,
  },
  meta: {
    id: 'meta', name: 'Meta Ads', kind: 'Attribution',
    desc: 'Send conversion events to Meta for Instagram and Facebook ad pixel',
    logo: 'M', color: '#0866ff',
    connected: false,
  },
  shopify: {
    id: 'shopify', name: 'Shopify', kind: 'Inventory sync',
    desc: 'Two-way sync products, inventory, and orders with an existing Shopify store',
    logo: 'S', color: '#95bf47',
    connected: false,
  },
  printful: {
    id: 'printful', name: 'Printful', kind: 'Print-on-demand',
    desc: 'Fulfill merch without holding inventory · tees, hoodies, hats auto-ship',
    logo: 'P', color: '#ef4444',
    connected: false,
  },
  discord: {
    id: 'discord', name: 'Discord', kind: 'Community',
    desc: 'Auto-invite course buyers to your server · role gating for VIP drops',
    logo: 'D', color: '#5865f2',
    connected: false,
  },
  posthog: {
    id: 'posthog', name: 'PostHog', kind: 'Analytics',
    desc: 'Funnel analytics · see drop-off between steps · session replays for checkout',
    logo: 'ϕ', color: '#f54e00',
    connected: false,
  },
};
